import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { rawTimeZones /*, timeZonesNames, abbreviations */ } from '@vvo/tzdb';
import type { RawTimeZone } from '@vvo/tzdb';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

//#region 'Missing Data'
/*
This list was entered into GitHub as issue #46.
Phone prefixes and country abbreviation missing for: AX  Åland Islands
Phone prefixes and country abbreviation missing for: BQ  Bonaire Sint Eustatius Saba
Phone prefixes and country abbreviation missing for: BV  Bouvet Island
Phone prefixes and country abbreviation missing for: GF  French Guiana
Phone prefixes and country abbreviation missing for: GP  Guadeloupe
Phone prefixes and country abbreviation missing for: GS  South Georgia and the South Sandwich Islands
Phone prefixes and country abbreviation missing for: HM  Heard Island and McDonald Islands
Phone prefixes and country abbreviation missing for: MQ  Martinique
Phone prefixes and country abbreviation missing for: NF  Norfolk Island
Phone prefixes and country abbreviation missing for: TF  French Southern Territories
Phone prefixes and country abbreviation missing for: UM  United States Minor Outlying Islands
*/
//#endregion

// Do a global search for:  'shared   change this to '@shared
import {
    IsoCountriesService,
    IsoTimezonesService,
    PhoneService
} from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TIsoCountry, TIsoTimezones, TIsoCountryRawData, TSubDivisionsRawData, TTimezoneRawData } from '@shared/models';

type TImportOption = {
    'description': string;
    'dataFiles': string[];
    'method': string;
}

@Component({
    'selector': 'app-import',
    'templateUrl': './import.page.html',
    'styleUrls': ['./import.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule ]
})
export class ImportPage extends ViewBasePage implements OnInit, OnDestroy {
    public currentItem = {};
    public importInProgress = false;
    public selectedImport = null;
    public importOptions: TImportOption[] = [
        {
            'description': 'Merge the two data files then import to the collection isoCountries.',
            'dataFiles': ['assets/data/isoCountries.json', '../assets/data/subDivisions.json'],
            'method': 'buildIsoCountries'
        },
        {
            'description': 'Reshape the dataFile then import to the collection isoTimezones.',
            'dataFiles': ['assets/data/isoTimezones.json'],
            'method': 'buildIsoTimezones'
        }
    ];

    constructor(private isoTimezonesService: IsoTimezonesService, private isoCountriesService: IsoCountriesService,
        private phoneService: PhoneService, injector: Injector) {
        super(injector);
    }

    public override ngOnInit(): void {
        super.ngOnInit();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public onChooseImportOptionClick(option) {
        this.selectedImport = option;
    }

    public async onStartImportClick(): Promise<void> {
        this.importInProgress = true;
        this.uxHelperService.showSpinner();

        switch (this.selectedImport.method) {
            case 'buildIsoCountries':
                await this.buildIsoCountries();
                break;

            case 'buildIsoTimezones':
                await this.buildIsoTimezones();
                break;
        }
        this.uxHelperService.hideSpinner();
        this.importInProgress = false;
    }

    private async buildIsoCountries(): Promise<void> {
        try {
            const PSTs = this.getRawTimezoneByAbbr('PST');
            // console.log(PSTs);

            // const collection = this.firestorePathsService.getIsoCountriesCol();
            // const uidColumn  = 'isoCountryCode';

            let filename = '../assets/data/isoCountries.json';
            const countriesData = await this.localDataService.readFile(filename) as TIsoCountryRawData[];
            // console.log(countriesData);

            filename = '../assets/data/subDivisions.json';
            const subdivisionsData = await this.localDataService.readFile(filename) as TSubDivisionsRawData[];
            // console.log(subdivisionsData);

            if (countriesData.length < 1) {
                return;
            }

            let count = 0;
            for (const item of countriesData) {
                ++count;

                const isoCountryCode = item.isoCountryCode;
                const subDivisions = subdivisionsData.filter(item => {
                    const prefix = isoCountryCode + '-';
                    if (item.value.includes(prefix)) {
                        return item;
                    }
                    return null;
                });
                const regions = subDivisions.map(r => {
                    const region = {
                        'regionName': r.label,
                        'regionCode': r.value
                    };
                    return region;
                });

                const countryPhoneData = this.phoneService.getPhoneInfo(isoCountryCode);
                // console.log(countryPhoneData);
                const phonePrefixes = countryPhoneData.phonePrefixes.map(prefix => '+' + prefix);

                if (!JSON.stringify(countryPhoneData).includes('unknown')) {
                    const country: TIsoCountry = {
                        'isoCountryCode': isoCountryCode,
                        'countryName': item.label,
                        'countryAbbr': countryPhoneData.countryAbbr,
                        'phonePrefixes': phonePrefixes,
                        'regions': regions,
                        'uid': isoCountryCode
                    }

                    await this.isoCountriesService.set(country)
                    // await this.firestoreService.set(collection, country);
                    // console.log(`Phone prefixes and country abbreviation missing for: ${isoCountryCode}  ${item.label}`);
                } else {
                    await this.uxHelperService.showAlert('Info', count + ' documents have been imported.');
                }
            }
        } catch (err) {
            // console.error(err);
        }
    }

    private async buildIsoTimezones(): Promise<void> {
        // console.log('timezone = ', timezone);
        // console.log('rawTimezones = ', rawTimeZones);

        try {
            this.uxHelperService.showSpinner();

            const filename = '../assets/data/isoTimezones.json';
            const timezonesData = await this.localDataService.readFile(filename) as TTimezoneRawData[];
            // console.log(timezonesData);

            // 
            const timezones = timezonesData.map(item => {
                const offset = item.caption.toString().substring(0, 6);
                const timezone = {
                    'offset': offset,
                    'timezoneName': item.value,
                    'caption': item.caption
                };
                return timezone;
            });

            const obj: TIsoTimezones = {
                'timezones': timezones,
                'uid': 'allTimezones'
            };
            // console.log(obj);
            await this.isoTimezonesService.set(obj);

            this.uxHelperService.hideSpinner();
            await this.uxHelperService.showAlert('Info', '1 document has been imported.');
        } catch (err) {
            // console.error(err);
        }
    }

    // public getTimezones(): any {
    //     const timeZonesWithUtc = getTimeZones({ includeUtc: true });
    //     return timeZonesWithUtc;
    // }

    public getRawTimezoneByAbbr(abbreviation: string): RawTimeZone[] {
        // console.log('rawTimeszones = ', rawTimeZones);
        const timezones = rawTimeZones.filter(item => {
            if (item.abbreviation === abbreviation) {
                return item;
            }
            return null;
        });
        return timezones;
    }
}

